<template>
	<v-textarea
		ref="textareaRef"
		v-bind="{ ...safeAttrs, ...counterBindings }"
		:model-value="modelValueOrEmpty"
		:rules="mergedRules"
		@update:model-value="(value) => emit('update:modelValue', value ?? '')">
		<template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
			<slot :name="slotName" v-bind="slotProps ?? {}" />
		</template>
	</v-textarea>
</template>

<script setup lang="ts">
	import { computed, onMounted, onUpdated, ref, useAttrs } from "vue";
	import { counterFor, type CharacterCountUnit } from "../utilities/characterCount";

	defineOptions({ inheritAttrs: false });

	const props = withDefaults(
		defineProps<{
			modelValue: string | null | undefined;
			characterLimit?: number;
			counterUnit?: CharacterCountUnit;
			// Escape hatch for a field whose SERVER counts something the three CharacterCountUnits can't
			// express — currently only the quick-action message, where the server expands
			// ${organization} / ${user} placeholders before measuring (LSP-17482), so the honest count is
			// a function of component state, not just of the typed text. Applies to BOTH the visible
			// counter and the over-limit rule, on purpose: a counter that disagrees with the rule that
			// blocks submit is the bug this prop exists to fix. Prefer counterUnit for everything else.
			measureValue?: (value: string) => number;
			rules?: Array<(value: string) => boolean | string>;
			overLimitMessage?: string;
		}>(),
		{ counterUnit: "codepoints", rules: () => [] }
	);

	const emit = defineEmits<{
		(event: "update:modelValue", value: string): void;
	}>();

	const textareaRef = ref<any>(null);

	// Normalize inbound so every caller doesn't have to guard `modelValue ?? ""` themselves — this
	// mirrors the outbound `value ?? ''` on the update:modelValue emit below (LSP-17265 review).
	const modelValueOrEmpty = computed(() => props.modelValue ?? "");

	const attrs = useAttrs();

	// Strip `maxlength`/`maxLength` out of the attrs actually bound to the inner <v-textarea>. A
	// caller passing either would hard-cap the native textarea, silently defeating the warn-don't-cap
	// contract this component exists to enforce (LSP-16465). Warns so the caller learns why their attr
	// had no effect (LSP-17265 review, Finding A).
	// Matched on the lowercased key rather than by listing spellings: HTML attribute names are
	// case-insensitive, so `MAXLENGTH` / `MaxLength` would otherwise slip past an exact-key filter and
	// reach the DOM as a real native maxlength. break-it round 1
	//
	// Vue's forced-binding modifiers are also stripped first: `:maxlength.attr` arrives in $attrs keyed
	// as `^maxlength` and `:maxlength.prop` as `.maxlength`, either of which sails past a plain
	// lowercase compare and installs a hard cap. break-it round 5
	const isMaxLengthAttr = (attrName: string): boolean => attrName.replace(/^[\^.]/, "").toLowerCase() === "maxlength";

	const safeAttrs = computed<Record<string, unknown>>(() =>
		Object.fromEntries(Object.entries(attrs).filter(([attrName]) => !isMaxLengthAttr(attrName)))
	);

	// Checked on mount AND after every re-render: a caller can bind maxlength conditionally, so the attr
	// may first appear later — it was still stripped, but silently, which is the shape of failure this
	// warning exists to prevent. $attrs is deliberately NOT reactive in Vue (it is replaced per render),
	// so a watcher over it never fires; onUpdated is the supported hook. Warned once per component
	// instance — not once per spelling — so a caller toggling or re-casing the attr can't spam the
	// console. One warning is enough to convey the rule. break-it round 3
	let hasWarnedAboutMaxLengthAttr = false;
	const warnAboutStrippedMaxLengthAttr = (): void => {
		if (hasWarnedAboutMaxLengthAttr) return;
		// Report the caller's actual spelling, and stay in step with the filter above so no casing is
		// stripped silently.
		const droppedAttrName = Object.keys(attrs).find((attrName) => isMaxLengthAttr(attrName) && attrs[attrName] !== undefined) ?? null;
		if (!droppedAttrName) return;
		hasWarnedAboutMaxLengthAttr = true;
		console.warn(
			`[AppTextarea] Ignoring "${droppedAttrName}" — this component enforces limits via a counter + validation rule ` +
				`(warn, never hard-cap; LSP-16465), never the native maxlength attribute. Pass "character-limit" instead.`
		);
	};
	onMounted(warnAboutStrippedMaxLengthAttr);
	onUpdated(warnAboutStrippedMaxLengthAttr);

	// Warn-and-degrade rather than throw if a caller passes a non-function, matching how counterFor
	// handles a mis-specified unit: the two callers are plain Options-API `<script>` blocks with no
	// `lang="ts"` and contact-admin runs no vue-tsc in CI, so a typo'd binding ships clean and would
	// otherwise blow up inside a computed during render and blank the whole dialog.
	const countValue = computed(() => {
		if (props.measureValue == null) return counterFor(props.counterUnit);
		if (typeof props.measureValue !== "function") {
			console.error(
				`[AppTextarea] Ignoring non-function "measure-value" (${typeof props.measureValue}) — counting by counter-unit instead.`
			);
			return counterFor(props.counterUnit);
		}
		// A measure-value that THROWS gets the same treatment as one that is not a function at all: it is
		// invoked from Vuetify's counter render and from the over-limit rule, so an unguarded throw takes
		// the whole field — and in the quick-action dialogs the whole dialog — down. Guarding only the
		// non-function case would have been asymmetric against this block's own justification.
		// break-it round 1
		const measure = props.measureValue;
		const fallback = counterFor(props.counterUnit);
		return (value: string) => {
			try {
				return measure(value);
			} catch (error) {
				console.error(`[AppTextarea] "measure-value" threw — counting by counter-unit instead.`, error);
				return fallback(value);
			}
		};
	});

	// A defined counter-value forces Vuetify to render a counter, so only expose the counting fn
	// when a limit is actually set — otherwise the field renders exactly like a bare v-textarea.
	//
	// These are spread INTO the same v-bind as safeAttrs (never bound as separate `:counter` /
	// `:counter-value` attributes after it): an explicit binding placed after `v-bind="safeAttrs"`
	// wins for that key, so in plain mode it would bind `undefined` and wipe a caller-supplied
	// counter/counter-value coming through attrs — breaking the transparent-wrapper contract
	// (Bugbot, PR #3081). Spreading conditionally means plain mode adds nothing at all.
	const counterBindings = computed<Record<string, unknown>>(() =>
		props.characterLimit == null ? {} : { counter: props.characterLimit, counterValue: countValue.value }
	);

	// Merge the caller's rules with the over-limit rule (only when a limit is set). rules is a real
	// prop, not $attrs, so this merge can't be overwritten by Vue's attribute fallthrough.
	const mergedRules = computed<Array<(value: string) => boolean | string>>(() => {
		if (props.characterLimit == null) return props.rules;
		const limit = props.characterLimit;
		const message = props.overLimitMessage ?? "Please enter shorter text.";
		const overLimitRule = (value: string) => countValue.value(value ?? "") <= limit || message;
		return [...props.rules, overLimitRule];
	});

	// Preserve the DOM-access contract consumers relied on when they used ref= on a raw v-textarea:
	// expose the inner Vuetify ref, a focus() passthrough, and the native <textarea> element.
	defineExpose({
		textareaRef,
		focus: () => textareaRef.value?.focus?.(),
		get el(): HTMLTextAreaElement | null {
			const root = textareaRef.value?.$el as HTMLElement | undefined;
			return root?.querySelector?.("textarea") ?? null;
		}
	});
</script>
